import ClaimsOperationIcon, {
	Sign,
} from 'components/commons/ClaimIcons/ClaimsOperationIcon.component';
import {
	calculateEstimates,
	setCalculated,
	setOtherServices,
	setStatusCalculation,
	setValidationErrors,
} from 'feature/claims/claimSlice';
import { useClaimHook } from 'hooks/claim.hook';
import { uniqueId } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import styled from 'styled-components';
import {
	BoxOverflow,
	Button,
	Stack,
	Table,
	TableBody,
	TableCell as TableCellUnstyled,
	TableHead,
	TableRow,
	Typography,
} from 'styles';
import InputNumber from 'styles/input/InputNumber';
import { breakpoints } from 'styles/theme/Breakpoints';
import palette from 'styles/theme/Palette';
import {
	IClaim,
	IClaimComponents,
	SERVICES_TYPE,
	TStatusCalculation,
} from 'types/Claim';
import { IBenefit } from 'types/Member';
import { TRANSLATION_KEYS } from 'utils/constants';
import {
	formatPrice,
	getRangeValueAmount,
	roundingPrice100,
	serviceIdToLabel,
	useGetIsOtherComponent,
} from 'utils/utils';
import { useTranslation } from 'utils/translations';
import OtherItemsSection from './components/OtherItemsSection';
import StandardItemsSection from './components/StandardItemsSection';

type prescriptionProps = {
	formData: IClaim;
	showGross?: boolean;
};

type RowErrors = {
	grossAmount: boolean;
	maxValue: boolean;
	retailNotTaxAndNet: boolean;
	taxBiggerNet: boolean;
};

const NO_ERRORS: RowErrors = {
	grossAmount: false,
	maxValue: false,
	retailNotTaxAndNet: false,
	taxBiggerNet: false,
};

const PriceEstimate: FC<prescriptionProps> = props => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { t: tErrorLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.ERRORS,
	});
	const dispatch = useDispatch<AppDispatch>();
	const { showTaxColumns } = useClaimHook();

	const { formData, showGross = true } = props;
	const amount = formData.amount;

	const { calculated, validationErrors, statusCalculation, otherServices } =
		useSelector((state: RootState) => state.claim);
	const { member } = useSelector((state: RootState) => state.member);

	const [tempAmounts, setTempAmounts] = useState<IClaim['amount']>(amount);
	const [error, setError] = useState<RowErrors>(NO_ERRORS);
	const isThereErrors =
		error.grossAmount ||
		error.maxValue ||
		error.retailNotTaxAndNet ||
		error.taxBiggerNet;

	const benefit: {
		amountLimits: IBenefit['amountLimits'];
		serviceId: number;
	}[] = [
		...(member?.benefit?.exam ?? []),
		...(member?.benefit?.materials ?? []),
		...(member?.benefit?.others ?? []),
	].map(el => ({ amountLimits: el.amountLimits, serviceId: el.serviceId }));

	const { isOtherComponent } = useGetIsOtherComponent();

	const setStatus = useCallback(
		(newStatus: TStatusCalculation) => dispatch(setStatusCalculation(newStatus)),
		[dispatch],
	);

	useEffect(() => {
		const newOtherServices =
			member?.benefit?.others.map(el => ({
				serviceLabel: el.serviceLabel,
				serviceId: el.serviceId,
			})) || [];

		dispatch(setOtherServices(newOtherServices));
	}, [dispatch, member]);

	useEffect(() => {
		if (calculated) {
			setStatus('afterCalculation');
			dispatch(setCalculated(false));
		}
	}, [calculated, dispatch, amount, setStatus]);

	useEffect(() => {
		return () => {
			dispatch(setValidationErrors([]));
		};
	}, [dispatch]);

	const resetTempAmounts = useCallback(
		(amounts: IClaim['amount']) => {
			setTempAmounts(() => ({
				...amounts,
				components: amounts.components.map(el => {
					const base = {
						serviceId: el.serviceId,
						retailAmount: el.retailAmount,
						retailNetOfTax: showTaxColumns ? el.retailNetOfTax : '',
						tax: showTaxColumns ? el.tax : '',
						grossRetail: showGross ? el.grossRetail : 0,
					};
					return otherServices
						.map(otherEl => otherEl.serviceId)
						.includes(el.serviceId)
						? { ...base, rowId: uniqueId() }
						: base;
				}),
				total: {
					serviceId: null,
					retailAmount: '',
					retailNetOfTax: '',
					tax: '',
					grossRetail: 0,
				},
				accumulator: undefined,
			}));
		},
		[otherServices, showGross, showTaxColumns],
	);

	useEffect(() => {
		if (['onEdit', 'beforeCalculation'].includes(statusCalculation)) {
			resetTempAmounts(amount);
		} else {
			setTempAmounts(amount);
		}
	}, [amount, statusCalculation, resetTempAmounts]);

	const checkIsError = (row: IClaimComponents, forceChecks?: boolean) => {
		const rowError = { ...NO_ERRORS };

		if (showGross && (error.grossAmount || forceChecks)) {
			rowError.grossAmount =
				roundingPrice100(row.grossRetail || '') <
				roundingPrice100(row.retailAmount || '');
		}

		if (error.maxValue || forceChecks) {
			// without the gross column (rekey) the retail amount is checked against its own limit
			const maxField = showGross ? 'gross-retail-amount' : 'retail-amount';
			rowError.maxValue =
				roundingPrice100(
					showGross ? row.grossRetail || '' : row.retailAmount || '',
				) >
				roundingPrice100(
					getRangeValueAmount(row.serviceId, benefit, maxField).max || 0,
				);
		}

		if (['onEdit'].includes(statusCalculation) && showTaxColumns) {
			if (error.retailNotTaxAndNet || forceChecks) {
				rowError.retailNotTaxAndNet =
					roundingPrice100(row.retailNetOfTax || '') +
						roundingPrice100(row.tax || '') !==
					roundingPrice100(row.retailAmount || '');
			}
			if (error.taxBiggerNet || forceChecks) {
				rowError.taxBiggerNet =
					roundingPrice100(row.tax || '') >
					roundingPrice100(row.retailNetOfTax || '');
			}
		}

		return rowError;
	};

	const sumRowErrors = (components: IClaimComponents[]): RowErrors =>
		components.reduce(
			(sum, el) => {
				const errorsRow = checkIsError(el, true);
				return {
					grossAmount: errorsRow.grossAmount || sum.grossAmount,
					maxValue: errorsRow.maxValue || sum.maxValue,
					retailNotTaxAndNet: errorsRow.retailNotTaxAndNet || sum.retailNotTaxAndNet,
					taxBiggerNet: errorsRow.taxBiggerNet || sum.taxBiggerNet,
				};
			},
			{ ...NO_ERRORS },
		);

	// highlights the row when the BE validation (e.g. fee schedule) refers to its service
	const checkIsErrorBenefitId = (id: number | null) =>
		validationErrors.some(err => {
			const errorId = Number(err.field);
			return (
				errorId === id ||
				(errorId === SERVICES_TYPE.FRAME_AND_LENSES &&
					(id === SERVICES_TYPE.LENSES_ONLY || id === SERVICES_TYPE.FRAME_ONLY))
			);
		});

	const handleChangeAmounts = (
		formattedValue: string,
		serviceId: number | null,
		key: string,
	) => {
		const newComponents = tempAmounts.components.map(el => {
			if (el.serviceId === serviceId) {
				return {
					...el,
					[key]: formattedValue,
				};
			}

			return el;
		});

		setTempAmounts({
			...tempAmounts,
			components: newComponents,
		});

		// errors already shown disappear as soon as the values are fixed
		const errorsCalculate = sumRowErrors(newComponents);
		setError(prev => ({
			grossAmount: prev.grossAmount && errorsCalculate.grossAmount,
			maxValue: prev.maxValue && errorsCalculate.maxValue,
			retailNotTaxAndNet:
				prev.retailNotTaxAndNet && errorsCalculate.retailNotTaxAndNet,
			taxBiggerNet: prev.taxBiggerNet && errorsCalculate.taxBiggerNet,
		}));
	};

	const addCurrency = (value: number | string | undefined) => {
		return ['afterCalculation'].includes(statusCalculation)
			? formatPrice(value ?? '').replace('€', '€ ')
			: '';
	};

	const handleCalculate = () => {
		const errorsCalculate = sumRowErrors(tempAmounts.components);
		setError(errorsCalculate);

		if (
			!errorsCalculate.grossAmount &&
			!errorsCalculate.maxValue &&
			!errorsCalculate.retailNotTaxAndNet &&
			!errorsCalculate.taxBiggerNet
		) {
			const newFormData = Object.keys(formData)
				.filter(objKey => objKey !== 'claimUUID')
				.reduce((newObj: any, key: keyof IClaim | string) => {
					newObj[key] = formData[key as keyof IClaim];
					return newObj;
				}, {});

			// ignore empty rows
			const newComponents = tempAmounts.components.filter(el => el.serviceId);
			const newTempAmounts = { ...tempAmounts, components: newComponents };

			setTempAmounts(newTempAmounts);
			dispatch(calculateEstimates({ ...newFormData, amount: newTempAmounts }));
		}
	};

	const totals = tempAmounts.total;
	const otherComponents = tempAmounts.components.filter(el =>
		isOtherComponent(el),
	);

	// BE validation messages can be repeated: show each one only once
	const validationErrorsTexts = validationErrors.map(err =>
		tErrorLabels(err.message, {
			service: tClientLabels(serviceIdToLabel(Number(err.field))),
			feeSchedule: formatPrice(err.value),
			feeSchedulePercentageError: err.value,
		}),
	);
	const validationErrorsFiltered = validationErrorsTexts.filter(
		(el, index) => validationErrorsTexts.indexOf(el) === index,
	);

	return (
		<Stack gap="30px">
			<BoxOverflow variant="x">
				<Stack gap="20px" sx={{ minWidth: 1620 }}>
					<Stack justifyContent="space-between" direction="row">
						<Typography variant="title6">
							{tClientLabels('newClaim.selectedPage.sectionTitile.priceEstimate')}
						</Typography>
						{['afterCalculation'].includes(statusCalculation) ? (
							<Button
								variant="linkDark"
								onClick={() => setStatus('onEdit')}
								style={{ display: amount.total.total ? 'flex' : 'none' }}
							>
								{tClientLabels('newClaim.priceEstimate.edit')}
							</Button>
						) : null}
					</Stack>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>
									<Typography variant="bodyLink" sx={{ pl: '10px' }}>
										{tClientLabels('newClaim.priceEstimate.visionProduct')}
									</Typography>
								</TableCell>
								{showGross ? (
									<>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.grossRetail')}
											</Typography>
										</TableCell>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.MINUS} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.inStorePromotion')}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<ClaimsOperationIcon sign={Sign.EQUAL} />
										</TableCell>
									</>
								) : null}
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.retailAmount')}
									</Typography>
								</TableCell>
								{showTaxColumns ? (
									<>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.ARROW} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.retailNetOfTax')}
											</Typography>
										</TableCell>
										<TableCell align="center" $smallIcon>
											<ClaimsOperationIcon sign={Sign.AND} />
										</TableCell>
										<TableCell align="center">
											<Typography variant="bodyLink">
												{tClientLabels('newClaim.priceEstimate.tax')}
											</Typography>
										</TableCell>
									</>
								) : null}
								<TableCell align="center" $smallIcon>
									<ClaimsOperationIcon sign={Sign.MINUS} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.contractualAdjustments')}
									</Typography>
								</TableCell>
								<TableCell align="center">
									<ClaimsOperationIcon sign={Sign.EQUAL} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.memberPay')}
									</Typography>
								</TableCell>
								<TableCell align="center" $smallIcon>
									<ClaimsOperationIcon sign={Sign.PLUS} />
								</TableCell>
								<TableCell align="center">
									<Typography variant="bodyLink">
										{tClientLabels('newClaim.priceEstimate.planPay')}
									</Typography>
								</TableCell>
								{['beforeCalculation', 'onEdit'].includes(statusCalculation) &&
								otherComponents.length > 0 ? (
									<TableCell
										align="center"
										sx={{
											backgroundColor: theme => theme.palette.grey6.main,
											position: 'sticky',
											right: 0,
											width: 44,
										}}
									></TableCell>
								) : null}
							</TableRow>
						</TableHead>
						<TableBody>
							<StandardItemsSection
								error={error}
								tempAmounts={tempAmounts}
								setTempAmounts={setTempAmounts}
								checkIsError={checkIsError}
								handleChangeAmounts={handleChangeAmounts}
								isOtherComponent={isOtherComponent}
								addCurrency={addCurrency}
								showGross={showGross}
								status={statusCalculation}
								checkIsErrorBenefitId={checkIsErrorBenefitId}
								showTaxColumns={showTaxColumns}
							/>
							<OtherItemsSection
								error={error}
								tempAmounts={tempAmounts}
								setTempAmounts={setTempAmounts}
								checkIsError={checkIsError}
								handleChangeAmounts={handleChangeAmounts}
								isOtherComponent={isOtherComponent}
								addCurrency={addCurrency}
								showTaxColumns={showTaxColumns}
								status={statusCalculation}
								otherServices={otherServices}
								showGross={showGross}
							/>
							{!['beforeCalculation', 'onEdit'].includes(statusCalculation) && (
								<TableRow sx={{ height: { sm: 56, lg: 70 } }}>
									<TableCell sx={{ width: { sm: 136, lg: 230 } }}>
										<Typography variant="bodyLink" sx={{ pl: '10px' }}>
											{tClientLabels('newClaim.priceEstimate.totals')}:
										</Typography>
									</TableCell>
									{showGross ? (
										<>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.grossRetail)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.inStorePromotion)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
										</>
									) : null}
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.retailAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon></TableCell>
									{showTaxColumns ? (
										<>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.retailNetOfTax)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
											<TableCell align="center">
												<Typography variant="bodyLink">
													{addCurrency(totals.tax)}
												</Typography>
											</TableCell>
											<TableCell align="center" $smallIcon></TableCell>
										</>
									) : null}
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.contractualAdjustmentAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon>
										<Typography variant="bodyLink" style={{ color: palette.grey2.main }}>
											{addCurrency(totals.discountedAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.memberPayAmount)}
										</Typography>
									</TableCell>
									<TableCell align="center" $smallIcon></TableCell>
									<TableCell align="center">
										<Typography variant="bodyLink">
											{addCurrency(totals.allowablePlanPay)}
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Stack>
				{(validationErrorsFiltered.length > 0 || isThereErrors) && (
					<Stack mt={otherComponents.length > 0 ? '20px' : 0}>
						{validationErrorsFiltered.map((err, idx) => (
							<Typography key={idx} variant="bodyLink" color={palette.error.main}>
								{err}
							</Typography>
						))}
						{error.grossAmount && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.grossRetailError')}
							</Typography>
						)}
						{error.maxValue && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.maxAmountError')}
							</Typography>
						)}
						{error.retailNotTaxAndNet && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.retailNotTaxAndNetError')}
							</Typography>
						)}
						{error.taxBiggerNet && (
							<Typography variant="bodyLink" color={palette.error.main}>
								{tClientLabels('newClaim.priceEstimate.taxBiggerNetError')}
							</Typography>
						)}
					</Stack>
				)}
			</BoxOverflow>
			{['beforeCalculation', 'onEdit'].includes(statusCalculation) ? (
				<Stack
					direction={'row'}
					gap="20px"
					style={{
						marginTop:
							otherComponents.length > 0 ||
							validationErrorsFiltered.length > 0 ||
							isThereErrors
								? '20px'
								: 0,
					}}
				>
					{['onEdit'].includes(statusCalculation) ? (
						<Button
							variant="outlined"
							onClick={() => {
								setStatus('afterCalculation');
								setTempAmounts(amount);
								setError(NO_ERRORS);
								dispatch(setValidationErrors([]));
							}}
						>
							{tClientLabels('commons.buttons.cancel')}
						</Button>
					) : null}
					<Button
						disabled={Boolean(
							tempAmounts.components.find(
								el =>
									el.serviceId && (!el.retailAmount || (!el.grossRetail && showGross)),
							),
						)}
						variant="contained"
						onClick={handleCalculate}
					>
						{tClientLabels('newClaim.priceEstimate.calculate')}
					</Button>
				</Stack>
			) : null}
		</Stack>
	);
};

type TableCellProps = typeof TableCellUnstyled & {
	$smallIcon?: boolean;
	$firstInRow?: boolean;
};

export const TableCell = styled(TableCellUnstyled)<TableCellProps>`
	&.MuiTableCell-head {
		padding: ${({ $smallIcon }) =>
			$smallIcon ? '8px 0px 8px 0px' : '8px 20px 8px 20px'};
		width: ${({ $smallIcon }) => ($smallIcon ? '24px' : 'auto')};
	}
	padding: ${({ $smallIcon }) =>
		$smallIcon ? '8px 0px 8px 0px' : '8px 20px 8px 20px'};
	@media (min-width: ${breakpoints.values.sm}px) {
		&.MuiTableCell-head {
			padding: ${({ $smallIcon }) =>
				$smallIcon ? '8px 0px 8px 0px' : '8px 10px 8px 10px'};
			width: ${({ $smallIcon }) => ($smallIcon ? '24px' : 'auto')};
		}
		padding: ${({ $smallIcon }) =>
			$smallIcon ? '8px 0px 8px 0px' : '8px 10px 8px 10px'};
	}
`;

export const OutlinedInput = styled(InputNumber)`
	width: 91px;
	height: 28px;
	border-radius: 4px;
	padding-right: 8px;
	input {
		text-align: right;
		margin-right: 0px;
		margin-bottom: 16px;
		padding-right: 0px;
		padding-left: 8px;
		font-weight: 400;
		font-size: 13px;
	}
	input::-webkit-outer-spin-button,
	input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	input[type='text'] {
		appearance: none;
		-moz-appearance: textfield;
	}
	.MuiInputBase-root,
	.MuiInputBase-root:hover,
	.MuiInputBase-root:focus {
		background-color: transparent !important;
	}
`;

export default PriceEstimate;
